import { useCallback, useEffect, useRef, useState } from 'react';

type TTSStatus = 'idle' | 'speaking' | 'paused';

const MAX_CHUNK_CHARS = 140;

function stripKatex(text: string): string {
    return text
        .replace(/[−–—]/g, '-')
        .replace(/\$\$([^$]+)\$\$/g, ' $1 ')
        .replace(/\$([^$]+)\$/g, ' $1 ')
        .replace(/\\\\/g, ' ')
        .replace(/\\(hspace|vspace|phantom|hphantom|vphantom|mkern|kern|rule|raisebox)\s*\{[^{}]*\}/g, ' ')
        .replace(/\\(text|mathrm|mathbf|mathit|mathsf|mathtt)\s*\{([^{}]+)\}/g, ' $2 ')
        .replace(/\\left\s*\|([^|]+?)\\right\s*\|/g, ' absolute value of $1 ')
        .replace(/\\lvert\s*([^|]+?)\\rvert/g, ' absolute value of $1 ')
        .replace(/\|([^|]+?)\|/g, ' absolute value of $1 ')
        .replace(/\\sqrt\s*\[(\d+)\]\s*\{([^{}]+)\}/g, ' $1th root of $2 ')
        .replace(/\\frac\s*\{((?:[^{}]|\{[^{}]*\})+)\}\s*\{((?:[^{}]|\{[^{}]*\})+)\}/g, ' $1 over $2 ')
        .replace(/\\sqrt\s*\{([^{}]+)\}/g, ' square root of $1 ')
        .replace(/\\pi\b/g, ' pi ')
        .replace(/\\theta\b/g, ' theta ')
        .replace(/\\alpha\b/g, ' alpha ')
        .replace(/\\beta\b/g, ' beta ')
        .replace(/\\sqrt\b/g, ' square root of ')
        .replace(/\\leq?\b/g, ' less than or equal to ')
        .replace(/\\geq?\b/g, ' greater than or equal to ')
        .replace(/\\neq\b/g, ' not equal to ')
        .replace(/\\times\b/g, ' times ')
        .replace(/\\div\b/g, ' divided by ')
        .replace(/\\cdot\b/g, ' times ')
        .replace(/\\[a-zA-Z]+/g, ' ')
        .replace(/\^2\b/g, ' squared ')
        .replace(/\^3\b/g, ' cubed ')
        .replace(/\^\{([^}]+)\}/g, ' to the $1 ')
        .replace(/\^(-?\d+)/g, ' to the $1 ')
        .replace(/π/g, ' pi ')
        .replace(/θ/g, ' theta ')
        .replace(/α/g, ' alpha ')
        .replace(/β/g, ' beta ')
        .replace(/≤/g, ' less than or equal to ')
        .replace(/≥/g, ' greater than or equal to ')
        .replace(/≠/g, ' not equal to ')
        .replace(/×/g, ' times ')
        .replace(/÷/g, ' divided by ')
        .replace(/\(/g, ' open parenthesis ')
        .replace(/\)/g, ' close parenthesis ')
        .replace(/[{}]/g, ' ')
        .replace(/_{2,}/g, ' blank ')
        .replace(/(\d),(?=\d{3}(?!\d))/g, '$1')
        .replace(/(^|[^a-zA-Z0-9)])-(\d|[a-zA-Z])/g, '$1negative $2')
        .replace(/([a-zA-Z0-9\)])\s*-\s*([a-zA-Z0-9\(])/g, '$1 minus $2')
        .replace(/\b[A-Z]{2,6}\b/g, m => m.split('').join(' '))
        .replace(/(\d)([a-zA-Z])/g, '$1 $2')
        .replace(/([a-zA-Z])(\d)/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
}

function chunkText(text: string): string[] {
    const cleaned = stripKatex(text);
    if (cleaned.length <= MAX_CHUNK_CHARS) return [cleaned];

    const sentences = cleaned.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [cleaned];
    const chunks: string[] = [];
    let current = '';

    for (const sentence of sentences) {
        const s = sentence.trim();
        if (!s) continue;
        if ((current + ' ' + s).length > MAX_CHUNK_CHARS && current) {
            chunks.push(current.trim());
            current = s;
        } else {
            current = current ? `${current} ${s}` : s;
        }
    }
    if (current) chunks.push(current.trim());
    return chunks;
}

function resolveVoice(): SpeechSynthesisVoice | null {
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;
    return (
        voices.find(v => v.lang === 'en-US' && v.default) ||
        voices.find(v => v.lang === 'en-US') ||
        voices.find(v => v.lang.startsWith('en-')) ||
        null
    );
}

export function useTTS() {
    const [status, setStatus] = useState<TTSStatus>('idle');
    const [rate, setRate] = useState<number>(() => {
        const saved = parseFloat(localStorage.getItem('ttsRate') || '1');
        return isNaN(saved) ? 1 : Math.min(2, Math.max(0.5, saved));
    });

    const voiceRef = useRef<SpeechSynthesisVoice | null>(null);
    const queueRef = useRef<string[]>([]);
    const chunkIndexRef = useRef<number>(0);
    const rateRef = useRef<number>(rate);
    const onCompleteRef = useRef<(() => void) | null>(null);
    const pausedFallbackRef = useRef<string[] | null>(null);

    useEffect(() => {
        rateRef.current = rate;
        localStorage.setItem('ttsRate', String(rate));
    }, [rate]);

    useEffect(() => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

        voiceRef.current = resolveVoice();
        if (!voiceRef.current) {
            const onVoicesChanged = () => {
                voiceRef.current = resolveVoice();
            };
            window.speechSynthesis.addEventListener('voiceschanged', onVoicesChanged);
            return () => window.speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
        const synth = window.speechSynthesis;
        // Reset any wedged state from prior page load (macOS Chrome speech-daemon bug)
        try {
            synth.cancel();
            if (synth.paused) synth.resume();
        } catch {
            // ignore — not all browsers implement all methods uniformly
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
        const interval = setInterval(() => {
            const synth = window.speechSynthesis;
            if (synth.speaking && !synth.paused) {
                synth.pause();
                synth.resume();
            }
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const onVisibility = () => {
            if (document.hidden && 'speechSynthesis' in window) {
                const synth = window.speechSynthesis;
                if (synth.speaking || synth.pending || synth.paused) {
                    synth.cancel();
                    queueRef.current = [];
                    chunkIndexRef.current = 0;
                    pausedFallbackRef.current = null;
                    setStatus('idle');
                }
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    const speakNextChunk = useCallback(() => {
        const queue = queueRef.current;
        const i = chunkIndexRef.current;
        if (i >= queue.length) {
            setStatus('idle');
            return;
        }
        const synth = window.speechSynthesis;
        if (synth.paused) synth.resume();

        const utter = new SpeechSynthesisUtterance(queue[i]);
        if (voiceRef.current) utter.voice = voiceRef.current;
        utter.rate = rateRef.current;
        utter.onend = () => {
            chunkIndexRef.current += 1;
            if (chunkIndexRef.current < queueRef.current.length) {
                speakNextChunk();
            } else {
                setStatus('idle');
                const cb = onCompleteRef.current;
                if (cb) cb();
            }
        };
        utter.onerror = () => {
            queueRef.current = [];
            chunkIndexRef.current = 0;
            setStatus('idle');
        };
        synth.speak(utter);
    }, []);

    const speak = useCallback((text: string) => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
        const chunks = chunkText(text);
        if (!chunks.length) return;
        const synth = window.speechSynthesis;
        if (synth.paused) synth.resume();

        pausedFallbackRef.current = null;

        const stuckState = synth.speaking && queueRef.current.length === 0;
        const wasActive = synth.speaking || synth.pending;
        if (stuckState || wasActive) {
            synth.cancel();
            setTimeout(() => {
                if (synth.paused) synth.resume();
                queueRef.current = chunks;
                chunkIndexRef.current = 0;
                setStatus('speaking');
                speakNextChunk();
            }, 120);
        } else {
            queueRef.current = chunks;
            chunkIndexRef.current = 0;
            setStatus('speaking');
            speakNextChunk();
        }
    }, [speakNextChunk]);

    const pause = useCallback(() => {
        if (!('speechSynthesis' in window)) return;
        const synth = window.speechSynthesis;
        if (synth.speaking && !synth.paused) {
            pausedFallbackRef.current = queueRef.current.slice(chunkIndexRef.current);
            synth.pause();
            setStatus('paused');
        }
    }, []);

    const resume = useCallback(() => {
        if (!('speechSynthesis' in window)) return;
        const synth = window.speechSynthesis;
        if (synth.paused) {
            synth.resume();
            pausedFallbackRef.current = null;
            setStatus('speaking');
        } else if (pausedFallbackRef.current && pausedFallbackRef.current.length) {
            const remaining = pausedFallbackRef.current;
            pausedFallbackRef.current = null;
            queueRef.current = remaining;
            chunkIndexRef.current = 0;
            setStatus('speaking');
            speakNextChunk();
        }
    }, [speakNextChunk]);

    const stop = useCallback(() => {
        if (!('speechSynthesis' in window)) return;
        const synth = window.speechSynthesis;
        if (synth.speaking || synth.pending || synth.paused) {
            synth.cancel();
        }
        queueRef.current = [];
        chunkIndexRef.current = 0;
        pausedFallbackRef.current = null;
        setStatus('idle');
    }, []);

    const changeRate = useCallback((next: number) => {
        const clamped = Math.min(2, Math.max(0.5, next));
        setRate(clamped);
        rateRef.current = clamped;

        if (status !== 'idle' && queueRef.current.length) {
            const remaining = queueRef.current.slice(chunkIndexRef.current).join(' ');
            const synth = window.speechSynthesis;
            if (synth.speaking || synth.pending || synth.paused) {
                synth.cancel();
            }
            queueRef.current = chunkText(remaining);
            chunkIndexRef.current = 0;
            setStatus('speaking');
            setTimeout(() => speakNextChunk(), 80);
        }
    }, [status, speakNextChunk]);

    const setOnComplete = useCallback((cb: (() => void) | null) => {
        onCompleteRef.current = cb;
    }, []);

    return {
        status,
        rate,
        speak,
        pause,
        resume,
        stop,
        setRate: changeRate,
        setOnComplete,
        isSpeaking: status === 'speaking',
        isPaused: status === 'paused'
    };
}
